import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { TenantService } from './tenant.service';

@Injectable({ providedIn: 'root' })
export class MeasurementsService {
  private get base() { return `${this.tenant.apiBaseUrl}/v1/tenant`; }
  constructor(private http: HttpClient, private tenant: TenantService) {}

  list(params?: any): Observable<any> {
    return this.http.get(`${this.base}/measurements`, { params });
  }
  get(id: number): Observable<any> {
    return this.http.get(`${this.base}/measurements/${id}`);
  }
  create(data: { name: string; is_active?: boolean }): Observable<any> {
    return this.http.post(`${this.base}/measurements`, data);
  }
  update(id: number, data: { name: string; is_active?: boolean }): Observable<any> {
    return this.http.put(`${this.base}/measurements/${id}`, data);
  }
  delete(id: number): Observable<any> {
    return this.http.delete(`${this.base}/measurements/${id}`);
  }
  bulkDelete(ids: number[]): Observable<any> {
    return this.http.delete(`${this.base}/measurements/bulk-destroy`, { body: { ids } });
  }

  /**
   * v1.6.813 → v1.6.826 — Create-or-update a measurement by name.
   *
   * v1.6.813 introduced this as a STOPGAP awaiting the backend
   * endpoint (Backlog #9 from session 14). The real endpoint
   * POST /v1/tenant/measurements/upsert-by-name shipped on the
   * backend per the OpenAPI spec Daniel shared; this method now
   * calls it directly.
   *
   * Backend contract (per OpenAPI):
   *   POST /v1/tenant/measurements/upsert-by-name
   *   body: { items: [{ name: string, is_active?: boolean }] }
   *   response: { data: { items: [{ ...outcome }], summary } }
   *   match: case-insensitive on name
   *   outcomes per item: 'created' | 'updated' | 'restored'
   *
   * We keep the same method name + return-shape signature
   * (`{id, name, outcome}`) as the v1.6.813 stopgap so existing
   * callers (`product-form.component.ts` line 2104 et al.) don't
   * need any changes. The outcome literal `'found-on-retry'` from
   * the stopgap is now subsumed by the backend's `'updated'` /
   * `'restored'` cases — kept in the union for back-compat (no
   * caller branches on it).
   *
   * Naming kept as `upsertByNameStopgap` for now so we don't churn
   * import sites on this ship; a follow-up rename to a cleaner
   * `upsertByName` is queued in long-running threads.
   */
  upsertByNameStopgap(name: string): Observable<{ id: number; name: string; outcome: 'created' | 'updated' | 'restored' | 'found-on-retry' }> {
    return new Observable(subscriber => {
      const trimmed = (name || '').trim();
      if (!trimmed) {
        subscriber.error(new Error('measurement name is empty'));
        return;
      }
      this.http.post(`${this.base}/measurements/upsert-by-name`, {
        items: [{ name: trimmed, is_active: true }],
      }).subscribe({
        next: (res: any) => {
          // Response shape per OpenAPI: { data: { items: [...], summary } }.
          // Each item carries the persisted measurement (id, name) and a
          // per-item outcome literal. We requested 1 item so we read items[0].
          const payload = res?.data ?? res;
          const item = Array.isArray(payload?.items) ? payload.items[0] : null;
          if (!item) {
            subscriber.error(new Error('upsert-by-name response had no items[0]'));
            return;
          }
          // The OpenAPI spec doesn't fully define UpsertByNameItemOutcome's
          // inner properties, but the practical shape per the v1.6.811
          // sister endpoint (ingredients) is `{ id, name, outcome, ... }`
          // possibly nested as `{ item: { id, name }, outcome }`. Read both.
          const id = item.id ?? item.item?.id ?? item.measurement?.id;
          const persistedName = item.name ?? item.item?.name ?? trimmed;
          const outcome = item.outcome ?? 'created';
          if (!id) {
            subscriber.error(new Error('upsert-by-name response item had no id'));
            return;
          }
          subscriber.next({ id, name: persistedName, outcome });
          subscriber.complete();
        },
        error: (err: any) => subscriber.error(err),
      });
    });
  }
}
